/**
 * Application theme colors and styles
 */

// Light theme colors - Industry standard for financial/business apps (Wise, Revolut style)
const lightColors = {
  primary: '#00D4AA', // Professional teal-green (similar to Wise)
  primaryDark: '#00B896', // Darker version for pressed states
  primaryLight: '#33DDB8', // Lighter version for hover states
  secondary: '#6C7B7F', // Professional gray for secondary actions
  background: '#FFFFFF', // Pure white background (industry standard)
  surface: '#FFFFFF', // White cards/surfaces
  card: '#FFFFFF', // White cards with subtle shadow
  text: '#0F1419', // Very dark gray (better than pure black)
  textSecondary: '#6C7B7F', // Medium gray for secondary text
  textMuted: '#9CA3AF', // Light gray for muted text
  border: '#E5E7EB', // Light gray for borders
  borderLight: '#F3F4F6', // Very light gray for subtle borders
  error: '#EF4444', // Professional red
  success: '#10B981', // Professional green
  warning: '#F59E0B', // Professional amber
  info: '#3B82F6', // Professional blue
  disabled: '#D1D5DB', // Gray for disabled states
  processingDot: '#00D4AA', // Primary color for processing indicators
  inputBackground: '#FFFFFF', // White input fields
  shadowColor: '#000000', // Black shadow
  placeholder: '#9CA3AF', // Light gray for placeholders
  // Additional colors for dashboard
  chartLine: '#E5E7EB', // Light gray dotted line for charts
  chartBackground: '#F9FAFB', // Very light gray for chart backgrounds
  accent: '#F59E0B', // Amber accent for highlights
  lightBackground: '#F9FAFB', // Very light gray for section backgrounds
  mediumBackground: '#F3F4F6', // Medium light gray for subtle backgrounds
  // Gradient colors - Subtle professional gradients
  gradientStart: '#FFFFFF', // Pure white start
  gradientEnd: '#F9FAFB', // Very subtle gray end
};

// Dark theme colors - Professional dark mode (industry standard)
const darkColors = {
  primary: '#00E6C3', // Brighter teal for dark mode visibility
  primaryDark: '#00B896', // Darker version for pressed states
  primaryLight: '#33EBC9', // Lighter version for hover states
  secondary: '#9CA3AF', // Light gray for secondary actions in dark mode
  background: '#111827', // Professional dark background (not pure black)
  surface: '#1F2937', // Dark gray for cards
  card: '#1F2937', // Dark gray for cards
  text: '#F9FAFB', // Off-white for text (easier on eyes than pure white)
  textSecondary: '#9CA3AF', // Light gray for secondary text
  textMuted: '#6B7280', // Medium gray for muted text
  border: '#374151', // Dark gray for borders
  borderLight: '#2D3748', // Slightly lighter for subtle borders
  error: '#F87171', // Professional red for dark mode
  success: '#34D399', // Professional green for dark mode
  warning: '#FBBF24', // Professional amber for dark mode
  info: '#60A5FA', // Professional blue for dark mode
  disabled: '#4B5563', // Gray for disabled states
  processingDot: '#00E6C3', // Primary color for processing indicators
  inputBackground: '#1F2937', // Dark input background
  shadowColor: '#000000', // Black shadow
  placeholder: '#6B7280', // Medium gray for placeholders
  // Additional colors for dashboard
  chartLine: '#374151', // Dark gray dotted line for charts
  chartBackground: '#0F172A', // Very dark gray for chart backgrounds
  accent: '#FBBF24', // Amber accent for highlights
  lightBackground: '#1F2937', // Dark gray for section backgrounds
  mediumBackground: '#374151', // Medium dark gray for subtle backgrounds
  // Gradient colors - Professional dark gradients
  gradientStart: '#111827', // Dark background start
  gradientEnd: '#1F2937', // Slightly lighter dark end
};

// Common styles and measurements
const commonTheme = {
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },
  borderRadius: {
    sm: 6,
    md: 12,
    lg: 16,
    xl: 24,
    pill: 9999,
  },
  fontSizes: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
    xl: 20,
    xxl: 24,
    xxxl: 30,
  },
  fontWeights: {
    regular: '400' as const,
    medium: '500' as const,
    semibold: '600' as const,
    bold: '700' as const,
    heavy: '800' as const,
  },
  shadows: {
    sm: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.04,
      shadowRadius: 2,
      elevation: 1,
    },
    md: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.06,
      shadowRadius: 4,
      elevation: 2,
    },
    lg: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.08,
      shadowRadius: 8,
      elevation: 4,
    },
    card: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 3,
      elevation: 2,
    },
    // Professional subtle shadow for important elements
    elevated: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.12,
      shadowRadius: 16,
      elevation: 8,
    },
  },
};

// Export light theme
export const lightTheme = {
  colors: lightColors,
  ...commonTheme,
};

// Export dark theme
export const darkTheme = {
  colors: darkColors,
  ...commonTheme,
};

// Default theme (used for compatibility)
export const theme = lightTheme;
